import { useMemo } from "react";
import { useQueries, type UseQueryResult } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useAuth } from "auth";
import { networkTypeSelector } from "store/userSliceHelpers";
import { useSettlementToken } from "features/tokens/useSettlementToken";
import { useIssuerMerchants } from "pages/issuer-page/useIssuerMerchants";
import { merchantLookupQuery } from "./useMerchantLookup";
import type { SpecificMerchant } from "utils/AxiosUtil";

export interface UseMerchantNamesReturn {
  /** Recent merchants plus every id the on-chain lookup has resolved. */
  namesMap: Record<string, string>;
  /** Ids whose name is still loading — render a placeholder, not the raw id. */
  pendingIds: ReadonlySet<string>;
}

const summarize = (results: UseQueryResult<SpecificMerchant | null>[]) =>
  results.map((r) => ({ name: r.data?.merchantName, pending: r.isPending }));

/**
 * The list form of RedeemItem's fallback: names come from the recent merchants first, and any id
 * that list misses is looked up on-chain. Pass a memoised `merchantIds`.
 */
export function useMerchantNames(merchantIds: string[]): UseMerchantNamesReturn {
  const signedIn = !!useAuth().user;
  const networkType = useSelector(networkTypeSelector);
  const { param: token } = useSettlementToken();
  const { merchantsMap, loadingMerchants } = useIssuerMerchants();

  const missing = useMemo(
    () =>
      Array.from(new Set(merchantIds)).filter((id) => id && !merchantsMap[id]),
    [merchantIds, merchantsMap],
  );

  const lookups = useQueries({
    queries: missing.map((id) => ({
      ...merchantLookupQuery(networkType, id, token),
      // Until the recent list lands every id looks missing, so hold off rather than look them all up.
      enabled: signedIn && !loadingMerchants,
    })),
    // Inline on purpose: a stable combine is skipped when only `enabled` flips, and that render then
    // reads the previous state and flashes the raw id. The output is still deep-compared, so stable.
    combine: (results) => summarize(results),
  });

  return useMemo(() => {
    const namesMap = { ...merchantsMap };
    const pendingIds = new Set<string>();
    missing.forEach((id, i) => {
      const name = lookups[i]?.name;
      if (name) namesMap[id] = name;
      else if (loadingMerchants || (signedIn && lookups[i]?.pending))
        pendingIds.add(id);
    });
    return { namesMap, pendingIds };
  }, [merchantsMap, missing, lookups, loadingMerchants, signedIn]);
}
